import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft, Clock, Award, BookOpen, TrendingUp,
  Calendar, Mail, Building,
} from 'lucide-react';
import { useRoleData } from '../../hooks/useRoleData';
import { analyticsAPI } from '../../services/api';
import { formatDate } from '../../utils/dateFormatter';
import './TraineeAnalytics.css';

const COLORS = ['#a595c7', '#2d1b69', '#113696', '#d1dc24', '#604da1'];

const formatTime = (seconds) => {
  if (!seconds) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const TraineeAnalytics = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { filteredUsers, filteredModules } = useRoleData();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  // Find trainee from local data as fallback
  const trainee = filteredUsers.find((u) =>
    String(u.username || '').toLowerCase() === String(username || '').toLowerCase()
  );

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const response = await analyticsAPI.getTraineeAnalytics(username);
        setAnalytics(response.data.data);
      } catch (err) {
        console.error('Failed to fetch trainee analytics:', err);
        // Build from local data as fallback
        if (trainee) {
          const enrolledMods = filteredModules.filter(m =>
            trainee.enrolledModules?.includes(m.id)
          );
          setAnalytics({
            trainee: {
              id: trainee.id,
              username: trainee.username,
              name: trainee.name,
              firstName: trainee.firstName,
              lastName: trainee.lastName,
              email: trainee.email,
              department: trainee.department,
              avatar: trainee.avatar,
              initials: trainee.initials,
              status: trainee.status,
              joinDate: trainee.joinDate,
              lastActive: trainee.lastActive,
              progress: trainee.progress,
            },
            overallProgress: trainee.progress || 0,
            totalModules: trainee.enrolledModules?.length || 0,
            completedModules: trainee.completedModules?.length || 0,
            totalTimeSpent: 0,
            moduleProgress: enrolledMods.map(m => ({
              moduleId: m.id,
              title: m.title,
              category: m.category,
              duration: m.duration,
              contentCount: m.contentCount || 0,
              moduleStatus: m.status,
              progress: trainee.completedModules?.includes(m.id) ? 100 :
                Math.round(Math.random() * 80 + 10),
              completed: trainee.completedModules?.includes(m.id),
              completedDate: trainee.completedModules?.includes(m.id) ? '2024-04-01' : null,
              enrolledDate: trainee.joinDate,
              lastAccessed: trainee.lastActive,
              timeSpentSeconds: Math.round(Math.random() * 7200),
            })),
            monthlyActivity: [],
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (username) fetchAnalytics();
  }, [username, trainee, filteredModules]);

  if (loading) {
    return (
      <div className="trainee-analytics">
        <div className="trainee-analytics__loading">Loading trainee analytics...</div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="trainee-analytics">
        <div className="trainee-analytics__empty">
          <p>Trainee not found.</p>
          <button className="btn btn-primary" onClick={() => navigate('/analytics')}>
            Back to Analytics
          </button>
        </div>
      </div>
    );
  }

  const { trainee: t, moduleProgress = [] } = analytics;

  const categoryData = moduleProgress.reduce((acc, m) => {
    const existing = acc.find(c => c.name === m.category);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ name: m.category, value: 1, fill: COLORS[acc.length % COLORS.length] });
    }
    return acc;
  }, []);

  return (
    <div className="trainee-analytics">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/analytics')}>
            <ArrowLeft size={16} />
            Back to Analytics
          </button>
          <h1>Trainee Analytics</h1>
        </div>
        {username && (
          <button
            className="btn btn-outline btn-sm"
            onClick={() => navigate(`/trainees/${encodeURIComponent(username)}`)}
          >
            <TrendingUp size={16} />
            Detailed VR analytics
          </button>
        )}
      </div>

      {/* Trainee Profile Card */}
      <div className="card trainee-analytics__profile">
        <div className="trainee-analytics__profile-left">
          <div className="trainee-analytics__avatar">
            {t.avatar ? (
              <img src={t.avatar} alt={t.name} />
            ) : (
              <span>{t.initials}</span>
            )}
          </div>
          <div className="trainee-analytics__profile-info">
            <h2>{t.name}</h2>
            {t.username && (
              <span className="trainee-analytics__username">@{t.username}</span>
            )}
            <div className="trainee-analytics__profile-meta">
              <span><Mail size={13} /> {t.email}</span>
              <span><Building size={13} /> {t.department}</span>
              <span><Calendar size={13} /> Joined {formatDate(t.joinDate)}</span>
            </div>
            <div className="trainee-analytics__badges">
              <span className={`badge ${t.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                {t.status}
              </span>
              <span className="badge badge-dark">Trainee</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="trainee-analytics__stats">
        {[
          { icon: TrendingUp, label: 'Overall Progress', value: `${analytics.overallProgress}%`, color: 'green' },
          { icon: BookOpen, label: 'Enrolled Modules', value: analytics.totalModules, color: 'blue' },
          { icon: Award, label: 'Completed', value: analytics.completedModules, color: 'dark' },
          { icon: Clock, label: 'Total Time', value: formatTime(analytics.totalTimeSpent), color: 'yellow' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className={`analytics__stat-card analytics__stat-card--${color}`}>
            <div className="analytics__stat-icon">
              <Icon size={20} />
            </div>
            <div>
              <span className="analytics__stat-value">{value}</span>
              <span className="analytics__stat-label">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="trainee-analytics__charts">
        {/* Module Progress Bar Chart */}
        <div className="card trainee-analytics__chart-card trainee-analytics__chart-card--wide">
          <div className="card-header">
            <h3>Module Progress</h3>
            <p className="text-sm text-muted">Progress across enrolled modules</p>
          </div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={moduleProgress} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="title"
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  interval={0}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} domain={[0, 100]} />
                <Tooltip
                  formatter={(v) => [`${v}%`, 'Progress']}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Bar dataKey="progress" name="Progress %" radius={[6, 6, 0, 0]}>
                  {moduleProgress.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.progress >= 100 ? '#91cc47' : entry.progress >= 50 ? '#d1dc24' : '#a595c7'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Pie */}
        {categoryData.length > 0 && (
          <div className="card trainee-analytics__chart-card">
            <div className="card-header">
              <h3>Module Categories</h3>
            </div>
            <div className="card-body" style={{ paddingTop: 0 }}>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name} (${value})`}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Module Details Table */}
      <div className="card">
        <div className="card-header">
          <h3>Module Details</h3>
          <span className="badge badge-dark">{moduleProgress.length} modules</span>
        </div>
        <div className="trainee-analytics__table">
          <div className="trainee-analytics__table-header">
            <span>Module</span>
            <span>Category</span>
            <span>Progress</span>
            <span>Time Spent</span>
            <span>Last Accessed</span>
            <span>Status</span>
          </div>
          {moduleProgress.map((m) => (
            <div key={m.moduleId} className="trainee-analytics__table-row">
              <div className="trainee-analytics__table-module">
                <div className="analytics__table-icon">
                  <BookOpen size={14} />
                </div>
                <span>{m.title}</span>
              </div>
              <span className="badge badge-dark">{m.category}</span>
              <div className="analytics__table-progress">
                <div className="progress-bar" style={{ height: 6, flex: 1 }}>
                  <div className="progress-bar-fill" style={{ width: `${m.progress}%` }} />
                </div>
                <span
                  className={`analytics__table-pct ${
                    m.progress >= 75 ? 'analytics__table-pct--high' :
                    m.progress >= 50 ? 'analytics__table-pct--mid' : 'analytics__table-pct--low'
                  }`}
                >
                  {m.progress}%
                </span>
              </div>
              <span className="trainee-analytics__table-cell">
                {formatTime(m.timeSpentSeconds)}
              </span>
              <span className="trainee-analytics__table-cell">
                {m.lastAccessed ? formatDate(m.lastAccessed) : 'Never'}
              </span>
              <span className={`badge ${m.completed ? 'badge-green' : 'badge-gray'}`}>
                {m.completed ? 'Completed' : 'In Progress'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TraineeAnalytics;
